
import React, { useState } from 'react';
import { Box, MenuItem, InputAdornment } from "@mui/material";
import IconReusable from '../../../components/IconReusable';
import { CustomSelect, TextField, CustomButton, CustomRadioGroup, CustomCheckbox, DatePicker } from '../../../v2/MUI_custom_component';
import CustomChip from '../../Components/CustomChip';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { alphaNumericInputValidation } from '../../Common/Validation/alphaNumeric';


const FieldInput = ({
  fieldType,
  options = [],
  suggestions = [],
  value,
  onChange,
  label,
  questionTitle,
  placeholder,
}) => {
  const [tagInput, setTagInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const MAX_TAGS = 5;
  const MAX_TAG_LENGTH = 30;

  // tags — free-form chip input for array values
  if (fieldType === "tags") {
    const tags = Array.isArray(value) ? value : [];
    const isMaxTagsReached = tags.length >= MAX_TAGS;
    const addTag = (rawValue) => {
      if (isMaxTagsReached) return;
      const trimmed = rawValue.trim();
      if (trimmed && !tags.includes(trimmed)) {
        onChange([...tags, trimmed]);
      }
      setTagInput("");
      setShowSuggestions(false);
    };
    const handleAdd = () => addTag(tagInput);
    const matchedSuggestions = tagInput.trim()
      ? suggestions
          .filter(
            (s) =>
              s.toLowerCase().includes(tagInput.trim().toLowerCase()) &&
              !tags.includes(s),
          )
          .slice(0, 5)
      : [];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {tags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {tags.map((tag, idx) => (
              <CustomChip
                key={idx}
                label={tag}
                selected={true}
                onDelete={() => onChange(tags.filter((_, i) => i !== idx))}
              />
            ))}
          </Box>
        )}
        <Box sx={{ position: "relative" }}>
          <TextField
            label={"Brands"}
            variant="outlined"
            size="small"
            fullWidth
            placeholder={
              isMaxTagsReached
                ? `Maximum ${MAX_TAGS} brands allowed`
                : (placeholder ?? "Enter brand name")
            }
            value={tagInput}
            disabled={isMaxTagsReached}
            inputProps={{ maxLength: MAX_TAG_LENGTH }}
            onChange={(e) => {
              setTagInput(
                alphaNumericInputValidation(e.target.value).slice(0, MAX_TAG_LENGTH),
              );
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <CustomButton
                      label="Add"
                      variant="text"
                      size="small"
                      disabled={isMaxTagsReached}
                      leftIcon={() => <IconReusable name="Add" size={20} />}
                      onClick={handleAdd}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />
          {showSuggestions && matchedSuggestions.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10,
                mt: 0.5,
                bgcolor: "#fff",
                borderRadius: 1,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                maxHeight: 180,
                overflowY: "auto",
              }}>
              {matchedSuggestions.map((suggestion) => (
                <Box
                  key={suggestion}
                  onMouseDown={() => addTag(suggestion)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    fontSize: 14,
                    "&:hover": { bgcolor: "#F5F5F5" },
                  }}>
                  {suggestion}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // radio — single-select using CustomRadioGroup
  if (fieldType === "radio") {
    return (
      <CustomRadioGroup
        options={options}
        value={value || ""}
        size="small"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // checkbox — multi-select using CustomCheckbox per option
  if (fieldType === "checkbox") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {options.map((opt) => {
          const checked = Array.isArray(value) && value.includes(opt.value);
          return (
            <CustomCheckbox
              size="small"
              key={opt.value}
              label={opt.label}
              checked={checked}
              onChange={() => {
                const current = Array.isArray(value) ? value : [];
                onChange(
                  checked
                    ? current.filter((v) => v !== opt.value)
                    : [...current, opt.value],
                );
              }}
            />
          );
        })}
      </Box>
    );
  }

  // select — dropdown
  if (fieldType === "select") {
    return (
      <CustomSelect
        fullWidth
        size="small"
        value={value ?? ""}
        placeholder={placeholder ?? ""}
        onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </CustomSelect>
    );
  }

  // date — date picker
  if (fieldType === "date") {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(newValue) => {
            const dateString = newValue ? newValue.format("YYYY-MM-DD") : "";
            onChange(dateString);
          }}
          disableFuture
          format="DD/MM/YYYY"
          selectableDate={dayjs()}
          placeholder="Select date"
        />
      </LocalizationProvider>
    );
  }

  // number — numeric input (0–30, digits only)
  if (fieldType === "number") {
    return (
      <TextField
        type="text"
        inputMode="numeric"
        variant="outlined"
        fullWidth
        size="small"
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange("");
          } else if (/^\d+$/.test(raw)) {
            const n = Number(raw);
            if (n >= 0 && n <= 30) {
              onChange(n);
            }
          }
        }}
        placeholder="Enter number here"
      />
    );
  }

  // textarea — multi-line
  if (fieldType === "textarea") {
    return (
      <TextField
        variant="outlined"
        fullWidth
        multiline
        size="small"
        value={value || ""}
        onChange={(e) => onChange(e.target.value.replace(/[^A-Za-z\s]/g, ""))}
        placeholder={placeholder ?? ""}
        sx={{
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
          },
          "& .MuiInputBase-input": {
            maxHeight: 80,
            overflow: "auto",
          },
        }}
      />
    );
  }
  // preferred_language — chips for preferred, dropdown for others, selected as chips (first)
  if (fieldType === "preferred_language") {
    const opts = options && typeof options === "object" ? options : {};
    const preferredOptions = Array.isArray(opts.preferred)
      ? opts.preferred
      : [];
    const otherOptions = Array.isArray(opts.others)
      ? opts.others
      : Array.isArray(options)
        ? options
        : [];
    const selected = Array.isArray(value) ? value[0] || "" : value || "";
    const handleSelect = (lang) => {
      if (selected !== lang) {
        onChange(lang);
      }
    };
    const handleRemove = () => {
      onChange("");
    };
    const unselectedPreferred = preferredOptions.filter(
      (opt) => opt.value !== selected,
    );
    const availableOthers = otherOptions.filter(
      (opt) => opt.value !== selected,
    );
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            overflowX: "auto",
            flexWrap: "nowrap",
            py: 1,
          }}>
          {selected && (
            <CustomChip
              key={selected}
              label={selected}
              selected={true}
              onDelete={handleRemove}
            />
          )}
          {unselectedPreferred.map((opt) => (
            <CustomChip
              key={opt.value}
              label={opt.label}
              selected={false}
              onClick={() => handleSelect(opt.value)}
            />
          ))}
        </Box>
        {availableOthers.length > 0 && (
          <CustomSelect
            fullWidth
            size="small"
            value={""}
            displayEmpty
            onChange={(e) => handleSelect(e.target.value)}
            placeholder={"Choose additional languages"}>
            {availableOthers.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </CustomSelect>
        )}
      </Box>
    );
  }

  // text (default) — single-line
  return (
    <TextField
      variant="outlined"
      fullWidth
      size="small"
      value={value || ""}
      onChange={(e) => onChange(alphaNumericInputValidation(e.target.value))}
    />
  );
};;;

export default FieldInput;
